import { Building2, ChevronRight, Loader2, Check } from 'lucide-react';
import { Button } from '../../../ui/common-ui/button';
import { useState, useEffect, useCallback } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../../ui/common-ui/dropdown-menu';
import { fetchTenants, TenantData } from '../services/cloudApi';
import { useUICollapse } from '../hooks/useUICollapse';
import { cn } from '../lib/utils';
import { setLocalStorageTenant } from '../../../../services/Helpers';

// LocalStorage helpers for consistent {"value": ...} format
const lsGetValue = (key: string): any => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'value' in parsed) return parsed.value ?? null;
  } catch { }
  return raw;
};

const lsSetValue = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify({ value }));
};

interface TenantSelectorProps {
  onTenantChange?: (tenantId: string, tenantName: string) => void;
}

interface TenantOption {
  id: string;
  name: string;
  tenant: TenantData;
}

export function CollapsibleTenantSelector({ onTenantChange }: TenantSelectorProps) {
  const { isCollapsed, setHoveredElement, hoveredElement } = useUICollapse();
  const [open, setOpen] = useState(false);

  const isHovered = hoveredElement === 'tenant';
  const showExpanded = !isCollapsed || isHovered || open;

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch tenants from API
  const loadTenants = useCallback(async () => {
    setIsLoading(true);
    try {
      const tenantsData = await fetchTenants();
      const formattedTenants = tenantsData.map((t: TenantData) => ({
        id: t.tenantName,
        name: t.tenantName,
        tenant: t,
      }));
      setTenants(formattedTenants);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      setTenants([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Force-close dropdown when the graph collapses the UI
  useEffect(() => {
    if (isCollapsed) setOpen(false);
  }, [isCollapsed]);

  useEffect(() => {
    if (tenants.length === 0) return;

    try {
      const latestTenant = lsGetValue('latestTenant');
      if (latestTenant && typeof latestTenant === 'object' && latestTenant.tenantName) {
        const exists = tenants.find(t => t.id === latestTenant.tenantName || t.name === latestTenant.tenantName);
        if (exists) {
          setSelectedTenant(exists);
          lsSetValue('selectedTenant', exists);
          return;
        }
      }

      const savedTenant = lsGetValue('selectedTenant');
      if (savedTenant && typeof savedTenant === 'object') {
        const exists = tenants.find(t => t.id === savedTenant.id);
        if (exists) {
          setSelectedTenant(exists);
          setLocalStorageTenant(exists.tenant);
          return;
        }
      }
      setSelectedTenant(tenants[0]);
      lsSetValue('selectedTenant', tenants[0]);
      setLocalStorageTenant(tenants[0].tenant);
    } catch {
      if (tenants.length > 0) {
        setSelectedTenant(tenants[0]);
        setLocalStorageTenant(tenants[0].tenant);
      }
    }
  }, [tenants]);

  const handleTenantSelect = (tenant: TenantOption) => {
    setSelectedTenant(tenant);
    lsSetValue('selectedTenant', tenant);
    setLocalStorageTenant(tenant.tenant);

    if (onTenantChange) {
      onTenantChange(tenant.id, tenant.name);
    }
  };

  const displayName = selectedTenant?.name || 'Select Tenant';
  const truncatedName = displayName.length > 24 ? `${displayName.slice(0, 22)}...` : displayName;

  return (
    <div
      className={cn(
        "glass-panel shadow-lg border border-border/50 overflow-hidden transition-[width,border-radius] duration-300 ease-out flex-shrink-0",
        showExpanded ? "w-80 rounded-2xl" : "w-14 rounded-xl"
      )}
      onMouseEnter={() => setHoveredElement('tenant')}
      onMouseLeave={() => {
        if (!open) setHoveredElement(null);
      }}
    >
      <DropdownMenu
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) setHoveredElement('tenant');
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "w-full h-14 px-2 overflow-hidden hover:bg-muted/50 transition-colors relative !justify-start",
              showExpanded ? "rounded-2xl" : "rounded-xl"
            )}
          >
            {/* Icon is always anchored to the left to avoid “dancing” during expand/collapse */}
            <div className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>

            {/* Text is absolutely positioned so icon never shifts due to layout reflow */}
            <div
              className={cn(
                "absolute left-14 right-10 top-1/2 -translate-y-1/2 overflow-hidden transition-opacity duration-200 ease-out",
                showExpanded ? "opacity-100" : "opacity-0 pointer-events-none"
              )}
            >
              <div className="flex flex-col items-start min-w-0 text-left">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider text-left">
                  Tenant
                </span>
                <span className="text-sm font-semibold text-foreground mt-0.5 truncate w-full text-left">
                  {truncatedName}
                </span>
              </div>
            </div>


            <ChevronRight
              className={cn(
                "absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground transition-opacity duration-200",
                showExpanded ? "opacity-100" : "opacity-0"
              )}
            />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-80 rounded-xl bg-card shadow-xl border border-border p-1"
          side="right"
          align="start"
          sideOffset={8}
        >
          <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider px-3 py-2">
            Available Tenants
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : tenants.length === 0 ? (
            <div className="py-4 px-3 text-xs text-muted-foreground text-center">
              No tenants found
            </div>
          ) : (
            tenants.map((tenant) => {
              const isSelected = selectedTenant?.id === tenant.id;
              return (
                <DropdownMenuItem
                  key={tenant.id}
                  onSelect={() => handleTenantSelect(tenant)}
                  className={`py-2.5 px-3 rounded-lg cursor-pointer text-foreground ${isSelected ? 'bg-primary/10' : 'hover:bg-muted'
                    }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="flex-1 font-medium">{tenant.name}</span>
                    {isSelected && <Check className="w-4 h-4 text-primary" />}
                  </div>
                </DropdownMenuItem>
              );
            })
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
